import React, { useEffect, useMemo, useState } from "react";
import { Page } from "@/components/PageConsole";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Filter, Search, HandCoins } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState, EmptyState } from "@/components/ListState";
import { useUnsavedGuard, DiscardDialog } from "@/components/useUnsavedGuard";
import {
  WerkbonStatusBadge, WERKBON_STATUS_LABELS, WERKBON_SOORT_LABELS, formatNlDate, formatEuro,
} from "@/components/Badges";
import { formatUren } from "@/lib/uren";
import { Code } from "@/components/Code";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

const OPEN_STATUSES = ["voorbereid", "uitgegeven"];

const emptyForm = {
  omschrijving: "", location_id: "", ruimte_id: "__none__", asset_id: "__none__",
  soort: "correctief", streefdatum: "", fault_id: null,
  // Round 26 §2:
  prioriteit: "P3", werkbeschrijving: "", oorzaak_id: "__none__",
  geplande_uren: "", geplande_start: "", geplande_eind: "",
};

export default function WerkbonnenList() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bonnen, setBonnen] = useState([]);
  const [locations, setLocations] = useState([]);
  const [ruimtes, setRuimtes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [oorzaken, setOorzaken] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scope, setScope] = useState(searchParams.get("scope") || "open");
  const [filters, setFilters] = useState({
    soort: searchParams.get("soort") || "all",
    location_id: searchParams.get("location_id") || "all",
    factureerbaar: searchParams.get("factureerbaar") || "all",
  });
  const [search, setSearch] = useState(searchParams.get("q") || "");
  // Arriving from a storing, an asset or a QR flow: prefilled create dialog.
  const prefill = useMemo(() => {
    if (searchParams.get("new") !== "1") return null;
    return {
      ...emptyForm,
      location_id: searchParams.get("loc") || "",
      ruimte_id: searchParams.get("ruimte") || "__none__",
      asset_id: searchParams.get("asset") || "__none__",
      omschrijving: searchParams.get("desc") || "",
      fault_id: searchParams.get("fault") || null,
      soort: searchParams.get("fault") ? "storing" : "correctief",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [open, setOpen] = useState(Boolean(prefill));
  const [form, setForm] = useState(prefill || emptyForm);
  const [baseline, setBaseline] = useState(prefill || emptyForm);
  const [saving, setSaving] = useState(false);
  const guard = useUnsavedGuard(JSON.stringify(form) !== JSON.stringify(baseline), () => setOpen(false));

  useEffect(() => {
    const p = {};
    if (scope !== "open") p.scope = scope;
    if (filters.soort !== "all") p.soort = filters.soort;
    if (filters.location_id !== "all") p.location_id = filters.location_id;
    if (filters.factureerbaar !== "all") p.factureerbaar = filters.factureerbaar;
    if (search.trim()) p.q = search.trim();
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, filters, search]);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const [b, loc, rui, ass, oz] = await Promise.all([
        api.get("/werkbonnen"), api.get("/locations"), api.get("/ruimtes"), api.get("/assets"),
        api.get("/oorzaken").catch(() => ({ data: [] })),
      ]);
      setBonnen(b.data); setLocations(loc.data); setRuimtes(rui.data); setAssets(ass.data);
      setOorzaken(oz.data);
    } catch (e) { setError(getErrorMessage(e, "Kan werkbonnen niet laden.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bonnen.filter((b) => {
      if (scope === "open" && !OPEN_STATUSES.includes(b.status)) return false;
      if (scope !== "open" && scope !== "all" && b.status !== scope) return false;
      if (filters.soort !== "all" && b.soort !== filters.soort) return false;
      if (filters.location_id !== "all" && b.location_id !== filters.location_id) return false;
      if (filters.factureerbaar === "aan" && !b.factureerbaar) return false;
      if (filters.factureerbaar === "uit" && b.factureerbaar) return false;
      if (q && ![b.werkbon_number, b.omschrijving, b.location_name].filter(Boolean).join(" ").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [bonnen, scope, filters, search]);

  const formRuimtes = useMemo(() => ruimtes.filter((r) => r.location_id === form.location_id), [ruimtes, form.location_id]);
  const formAssets = useMemo(
    () => assets.filter((a) => a.location_id === form.location_id
      && (form.ruimte_id === "__none__" || a.ruimte_id === form.ruimte_id)),
    [assets, form.location_id, form.ruimte_id]
  );

  const submit = async () => {
    if (!form.omschrijving.trim()) { toast.error("Zeg wat het werk is."); return; }
    if (!form.location_id) { toast.error("Kies een locatie."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/werkbonnen", {
        omschrijving: form.omschrijving,
        location_id: form.location_id,
        ruimte_id: form.ruimte_id === "__none__" ? null : form.ruimte_id,
        asset_id: form.asset_id === "__none__" ? null : form.asset_id,
        soort: form.soort,
        streefdatum: form.streefdatum || null,
        fault_id: form.fault_id,
        prioriteit: form.prioriteit || "P3",
        werkbeschrijving: form.werkbeschrijving || null,
        oorzaak_id: form.oorzaak_id === "__none__" ? null : form.oorzaak_id,
        geplande_start: form.geplande_start || null,
        geplande_eind: form.geplande_eind || null,
        geplande_uren: form.geplande_uren === "" ? null : Number(String(form.geplande_uren).replace(",", ".")),
      });
      toast.success(`Werkbon ${data.werkbon_number} aangemaakt`);
      setOpen(false); setForm(emptyForm); setBaseline(emptyForm);
      navigate(`/werkbonnen/${data.id}`);
    } catch (e) { toast.error(getErrorMessage(e, "Aanmaken mislukt.")); }
    finally { setSaving(false); }
  };

  const openCount = bonnen.filter((b) => OPEN_STATUSES.includes(b.status)).length;
  const facts = loading ? ["laden…"] : [`${displayed.length} getoond`, `${openCount} open`];
  const actions = [
    ...(canWrite() ? [{ key: "maken", label: "Werkbon maken", icon: Plus, variant: "primary",
      testid: "werkbon-create-button",
      onClick: () => { setForm(emptyForm); setBaseline(emptyForm); setOpen(true); } }] : []),
    { key: "uitgezet", label: "Uitgezet", icon: HandCoins, testid: "werkbon-uitgezet-link",
      onClick: () => navigate("/werkbonnen/uitgezet") },
  ];

  const plaats = (b) => [b.location_name, b.ruimte_name, b.spoor].filter(Boolean).join(" · ");

  return (
    <Page console={{ variant: "view", title: "Werkbonnen", facts, actions }}>
      <div className="space-y-4">

      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-64">
            <Search size={18} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="werkbonnen-search-input" className="h-9 pl-8" placeholder="Zoek op nummer, omschrijving of locatie" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="h-9 w-auto min-w-[190px]" data-testid="werkbon-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Openstaand (standaard)</SelectItem>
              <SelectItem value="all">Alles</SelectItem>
              {Object.entries(WERKBON_STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="flex items-center gap-1 text-data text-muted-foreground"><Filter size={14} /></span>
          <Select value={filters.soort} onValueChange={(v) => setFilters((f) => ({ ...f, soort: v }))}>
            <SelectTrigger className="h-8 w-auto min-w-[130px]" data-testid="werkbon-filter-soort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Soort: alle</SelectItem>
              {Object.entries(WERKBON_SOORT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.location_id} onValueChange={(v) => setFilters((f) => ({ ...f, location_id: v }))}>
            <SelectTrigger className="h-8 w-auto min-w-[130px]" data-testid="werkbon-filter-location"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Locatie: alle</SelectItem>
              {locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.factureerbaar} onValueChange={(v) => setFilters((f) => ({ ...f, factureerbaar: v }))}>
            <SelectTrigger className="h-8 w-auto min-w-[150px]" data-testid="werkbon-filter-factureerbaar"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Factureerbaar: alle</SelectItem>
              <SelectItem value="aan">Factureerbaar</SelectItem>
              <SelectItem value="uit">Niet factureerbaar</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {error && !loading ? (
        <ErrorState testid="werkbonnen-load-error" message={error} onRetry={load} />
      ) : loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <Card key={i} className="p-4"><Skeleton className="h-14 w-full" /></Card>)}
        </div>
      ) : displayed.length === 0 ? (
        <EmptyState
          testid="werkbonnen-empty"
          title={search.trim() ? "Geen werkbon past bij deze zoekopdracht" : (scope === "open" ? "Niets open" : "Nog geen werkbonnen")}
          purpose="Eén document draagt het werk: uren, materiaal en geld landen op de werkbon, en hij is factureerbaar tenzij iemand bewust anders zegt."
          action={canWrite() ? { label: "Werkbon maken", icon: Plus, onClick: () => { setForm(emptyForm); setBaseline(emptyForm); setOpen(true); } } : null}
        />
      ) : (
        <>
          <Card className="hidden sm:block">
            <Table data-testid="werkbonnen-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[130px]">Nummer</TableHead>
                  <TableHead>Omschrijving</TableHead>
                  <TableHead className="w-[200px]">Plaats</TableHead>
                  <TableHead className="w-[130px]">Status</TableHead>
                  <TableHead className="w-[80px] text-right">Uren</TableHead>
                  <TableHead className="w-[110px] text-right">Bedrag</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayed.map((b) => (
                  <TableRow key={b.id} data-testid={`werkbon-row-${b.id}`} className="cursor-pointer" onClick={() => navigate(`/werkbonnen/${b.id}`)}>
                    <TableCell><Code>{b.werkbon_number}</Code></TableCell>
                    <TableCell className="text-body">{b.omschrijving}</TableCell>
                    <TableCell className="text-body">{plaats(b)}</TableCell>
                    <TableCell><WerkbonStatusBadge status={b.status} /></TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-data">{b.uren_totaal > 0 ? formatUren(b.uren_totaal) : null}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-data">{b.bedrag_totaal != null && b.bedrag_totaal !== 0 ? formatEuro(b.bedrag_totaal) : null}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="space-y-2 sm:hidden" data-testid="werkbonnen-cardlist">
            {displayed.map((b) => (
              <Card key={b.id} data-testid={`werkbon-card-${b.id}`} className="cursor-pointer p-4 transition-colors hover:bg-accent" onClick={() => navigate(`/werkbonnen/${b.id}`)}>
                <div className="flex items-start justify-between gap-2">
                  <Code>{b.werkbon_number}</Code>
                  <Code size="small">{formatNlDate((b.created_at || "").slice(0, 10))}</Code>
                </div>
                <div className="mt-1 text-body text-foreground">{b.omschrijving}</div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-data text-muted-foreground">
                  <span>{plaats(b)}</span>
                  {b.uren_totaal > 0 && <span>{formatUren(b.uren_totaal)} uur</span>}
                  {b.bedrag_totaal != null && b.bedrag_totaal !== 0 && <span>{formatEuro(b.bedrag_totaal)}</span>}
                </div>
                <div className="mt-2"><WerkbonStatusBadge status={b.status} /></div>
              </Card>
            ))}
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={guard.requestClose}>
        <DialogContent className="max-w-lg" srDescription="Maak een nieuwe werkbon: wat is het werk, waar, en om welk soort werk gaat het.">
          <DiscardDialog guard={guard} />
          <DialogHeader><DialogTitle>Werkbon maken</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <Label>Wat is het werk? *</Label>
              <Textarea data-testid="werkbon-omschrijving-input" value={form.omschrijving} onChange={(e) => setForm((f) => ({ ...f, omschrijving: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Locatie *</Label>
              <Select value={form.location_id} onValueChange={(v) => setForm((f) => ({ ...f, location_id: v, ruimte_id: "__none__", asset_id: "__none__" }))}>
                <SelectTrigger data-testid="werkbon-location-select"><SelectValue placeholder="Kies locatie" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Waar precies?</Label>
                <Select value={form.ruimte_id} onValueChange={(v) => setForm((f) => ({ ...f, ruimte_id: v, asset_id: "__none__" }))} disabled={!form.location_id}>
                  <SelectTrigger data-testid="werkbon-ruimte-select"><SelectValue placeholder="Hele locatie" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Hele locatie</SelectItem>{formRuimtes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Soort</Label>
                <Select value={form.soort} onValueChange={(v) => setForm((f) => ({ ...f, soort: v }))}>
                  <SelectTrigger data-testid="werkbon-soort-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(WERKBON_SOORT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {/* Machine is optional on purpose: work on a pand or buitenterrein
                has no machine, and forcing one invented a lane once. */}
            <div className="space-y-1.5">
              <Label>Machine (optioneel)</Label>
              <Select value={form.asset_id} onValueChange={(v) => setForm((f) => ({ ...f, asset_id: v }))} disabled={!form.location_id}>
                <SelectTrigger data-testid="werkbon-asset-select"><SelectValue placeholder="Geen" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">Geen</SelectItem>{formAssets.map((a) => <SelectItem key={a.id} value={a.id}>{[a.name || `${a.brand} ${a.model}`.trim(), a.spoor].filter(Boolean).join(" · ") || a.asset_code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Wat moet er gebeuren? (instructie)</Label>
              <Textarea data-testid="werkbon-werkbeschrijving-input" rows={3} value={form.werkbeschrijving} onChange={(e) => setForm((f) => ({ ...f, werkbeschrijving: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Prioriteit</Label>
                <Select value={form.prioriteit} onValueChange={(v) => setForm((f) => ({ ...f, prioriteit: v }))}>
                  <SelectTrigger data-testid="werkbon-prioriteit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="P1">P1 · urgent</SelectItem>
                    <SelectItem value="P2">P2</SelectItem>
                    <SelectItem value="P3">P3</SelectItem>
                    <SelectItem value="P4">P4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Oorzaak (optioneel)</Label>
                <Select value={form.oorzaak_id} onValueChange={(v) => setForm((f) => ({ ...f, oorzaak_id: v }))}>
                  <SelectTrigger data-testid="werkbon-oorzaak-select"><SelectValue placeholder="Onbekend" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— nog geen —</SelectItem>
                    {oorzaken.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Geplande start</Label>
                <Input type="date" data-testid="werkbon-geplandestart-input" value={form.geplande_start} onChange={(e) => setForm((f) => ({ ...f, geplande_start: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Geplande eind</Label>
                <Input type="date" data-testid="werkbon-geplandeeind-input" value={form.geplande_eind} onChange={(e) => setForm((f) => ({ ...f, geplande_eind: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Geplande uren</Label>
                <Input inputMode="decimal" placeholder="2,5" data-testid="werkbon-geplandeuren-input" value={form.geplande_uren} onChange={(e) => setForm((f) => ({ ...f, geplande_uren: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Streefdatum (optioneel — deadline)</Label>
              <Input type="date" data-testid="werkbon-streefdatum-input" value={form.streefdatum} onChange={(e) => setForm((f) => ({ ...f, streefdatum: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => guard.requestClose(false)}>Annuleren</Button>
            <Button data-testid="werkbon-save-button" onClick={submit} disabled={saving}>{saving ? "Bezig..." : "Aanmaken"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Page>
  );
}
